// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a form component for managing organization-level ledger
 * account defaults. These accounts are used for tracking inventory and fixed assets
 * throughout their lifecycle in the accounting system.
 */
import {
  LedgerAccountType,
  ledgerAccountRecordSchema,
} from '@/account/api/types';
import { LEDGER_ACCOUNT_LABELS } from '@/account/constants/ledger-account-labels';
import {
  useOrganizationLedgerAccounts,
  useUpdateOrganizationLedgerAccounts,
} from '@/account/hooks/use-organization-ledger-accounts';
import { EducationalTooltip } from '@/components/educational-tooltip';
import { FormCard } from '@/components/form-card';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { InfoOutlined as InfoOutlinedIcon } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Control, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating the ledger accounts form.
 * All fields are optional strings to allow empty values.
 */
const formSchema = ledgerAccountRecordSchema;

/** Type representing the validated form data */
type FormData = z.infer<typeof formSchema>;

/**
 * Converts backend ledger account data (with nulls) to form default values (with empty strings).
 * This ensures the form fields are never null, making them controlled inputs.
 */
const convertLedgerAccountsToFormDefaults = (
  ledgerAccounts: Partial<Record<LedgerAccountType, string | null>>
): Record<LedgerAccountType, string> => {
  return Object.fromEntries(
    Object.entries(ledgerAccounts).map(([key, value]) => [key, value || ''])
  ) as Record<LedgerAccountType, string>;
};

interface LedgerAccountFieldProps {
  accountType: LedgerAccountType;
  control: Control<FormData>;
  disabled: boolean;
}

/**
 * A single ledger account input field with an educational tooltip.
 * The tooltip opens on click and displays rich information about the account.
 */
function LedgerAccountField(props: LedgerAccountFieldProps) {
  const { label, description, example } =
    LEDGER_ACCOUNT_LABELS[props.accountType];
  const tooltip = useDisclosure();
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null
  );

  return (
    <Grid item xs={12} sm={4}>
      <Stack direction="row" gap={1}>
        <FormTextField
          name={props.accountType}
          label={label + ' account'}
          control={props.control}
          disabled={props.disabled}
          placeholder={'ex: ' + example}
          sx={{ width: '100%' }}
        />
        <IconButton
          size="small"
          sx={{ padding: 0.5 }}
          tabIndex={-1}
          onClick={tooltip.open}
          ref={setAnchorElement}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <EducationalTooltip
          title={label}
          isOpen={tooltip.isOpen}
          onClose={tooltip.close}
          anchorElement={anchorElement}
        >
          {description}
        </EducationalTooltip>
      </Stack>
    </Grid>
  );
}

/**
 * Inventory account types grouped by subsection for display purposes.
 */
const INVENTORY_SUBSECTIONS = [
  {
    title: 'Inventory management',
    accounts: ['INV_INVENTORY', 'INV_CLEARING'] as LedgerAccountType[],
  },
  {
    title: 'Inventory expenses',
    accounts: ['INV_COGS', 'INV_WRITE_OFF'] as LedgerAccountType[],
  },
];

/**
 * Fixed asset account types grouped by subsection for display purposes.
 */
const FIXED_ASSET_SUBSECTIONS = [
  {
    title: 'Asset management',
    accounts: [
      'FA_FIXED_ASSETS',
      'FA_CAPITALIZED_DEPLOYMENT',
    ] as LedgerAccountType[],
  },
  {
    title: 'Depreciation',
    accounts: [
      'FA_DEPRECIATION',
      'FA_ACCUMULATED_DEPRECIATION',
    ] as LedgerAccountType[],
  },
  {
    title: 'Asset disposal',
    accounts: [
      'FA_DISPOSAL_CLEARING',
      'FA_DISPOSAL_GAIN',
      'FA_DISPOSAL_LOSS',
    ] as LedgerAccountType[],
  },
];

interface Props {
  /**
   * The organization key to manage ledger accounts for.
   */
  orgKey: string;
}

/**
 * A form component that allows users to configure their organization's ledger
 * account defaults. Displays all 11 account types grouped by category (Inventory
 * and Fixed Assets).
 *
 * Changes are saved only when explicitly submitted, and the form tracks dirty
 * state to enable/disable the save button appropriately. All 11 accounts are
 * sent to the backend on save, even if unchanged.
 */
export function OrganizationLedgerAccountsCard(props: Props) {
  const { data } = useOrganizationLedgerAccounts(props.orgKey);
  const updateMutation = useUpdateOrganizationLedgerAccounts();

  const { control, formState, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: convertLedgerAccountsToFormDefaults(data.ledgerAccounts),
  });

  const isSaveDisabled = updateMutation.isPending || !formState.isDirty;

  /**
   * Resets the form to its initial state, discarding any changes.
   */
  const handleReset = () => {
    reset();
  };

  /**
   * Handles form submission when the data is valid.
   * Sends all 11 accounts to the API, converting empty strings to null.
   */
  const handleValidSubmission: SubmitHandler<FormData> = (formData) => {
    // Convert empty strings to null for API
    const ledgerAccounts = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [
        key,
        value === '' ? null : value,
      ])
    ) as Record<LedgerAccountType, string | null>;

    updateMutation.mutate(
      {
        orgKey: props.orgKey,
        ledgerAccounts: { ledgerAccounts },
      },
      {
        onSuccess: () => {
          // Reset form with new values
          reset(formData);
        },
      }
    );
  };

  /**
   * Renders a single ledger account field with label and info icon tooltip.
   */
  const renderAccountField = (accountType: LedgerAccountType) => {
    return (
      <LedgerAccountField
        key={accountType}
        accountType={accountType}
        control={control}
        disabled={updateMutation.isPending}
      />
    );
  };

  /**
   * Renders a subsection with its title and account fields in a grid.
   */
  const renderSubsection = (
    title: string,
    accounts: LedgerAccountType[],
    showDisposalNote?: boolean
  ) => {
    return (
      <Box key={title} paddingX={2} paddingY={2}>
        <Paper variant="outlined" sx={{ padding: 2 }}>
          <Typography variant="body2" fontWeight={500} mb={2}>
            {title}
          </Typography>
          <Grid container spacing={2}>
            {accounts.map((accountType) => renderAccountField(accountType))}
          </Grid>
          {showDisposalNote && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontSize: '0.875rem' }}>
                Using a single gain/loss account such as{' '}
                <strong>&ldquo;Gain (loss) on disposal&rdquo;</strong>
              </AlertTitle>
              If you use a single account, simply enter the <em>same</em>{' '}
              account name for <em>both</em> gain and loss accounts. Hardfin
              credits gains and debits losses, so the account will balance to
              give you the net gain (loss) on disposal.
            </Alert>
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <FormCard
      title="Ledger accounts"
      isDirty={formState.isDirty}
      isDisabled={isSaveDisabled}
      onReset={handleReset}
      onSubmit={handleSubmit(handleValidSubmission)}
    >
      <SupportSnackbar
        isOpen={updateMutation.isError}
        message="An error occurred while updating ledger accounts."
        onClose={updateMutation.reset}
      />

      {/* Inventory Accounts Section */}
      <Box paddingX={2} paddingY={1.5}>
        <Typography variant="subtitle1" gutterBottom>
          Inventory accounts
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Accounts for tracking inventory from receipt to sale or write-off
        </Typography>
      </Box>
      {INVENTORY_SUBSECTIONS.map((subsection) =>
        renderSubsection(subsection.title, subsection.accounts)
      )}

      <Divider />

      {/* Fixed Asset Accounts Section */}
      <Box paddingX={2} paddingY={1.5}>
        <Typography variant="subtitle1" gutterBottom>
          Fixed asset accounts
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Accounts for tracking deployed equipment through its lifecycle
        </Typography>
      </Box>
      {FIXED_ASSET_SUBSECTIONS.map((subsection) =>
        renderSubsection(
          subsection.title,
          subsection.accounts,
          subsection.title === 'Asset disposal'
        )
      )}
    </FormCard>
  );
}
